import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./ManageEmployees.css";

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const API =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (isLocalhost ? "http://localhost:5003" : "https://erp-api.sdcllp.co.in");

const COMPANY_NAME = "Softvolute Buildcon LLP";

const authHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("softvolute_token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getEmptyForm = () => ({
  name: "",
  email: "",
  password: "",
  mobileNumber: "",
  dateOfBirth: "",
  joiningDate: new Date().toISOString().slice(0, 10),
  hourlyRate: "",
  role: "Designer",
});

const toInputDate = (value) => (value ? String(value).slice(0, 10) : "");

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB");
};

const formatRate = (value) =>
  value === undefined || value === null || value === ""
    ? "-"
    : `₹${Number(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const ManageEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(getEmptyForm());
  const [editingId, setEditingId] = useState("");
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [newRole, setNewRole] = useState("");
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleError, setRoleError] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const [employeeResponse, roleResponse] = await Promise.all([
        axios.get(`${API}/api/users/manage`, { headers: authHeaders() }),
        axios.get(`${API}/api/users/roles`, { headers: authHeaders() }),
      ]);

      setEmployees(employeeResponse.data?.data || []);
      setRoles(roleResponse.data?.data || []);
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Unable to load employees."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredEmployees = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return employees;

    return employees.filter((employee) =>
      [
        employee.name,
        employee.email,
        employee.mobileNumber,
        employee.role,
        employee.companyName,
      ]
        .map((item) => String(item || "").toLowerCase())
        .some((item) => item.includes(value))
    );
  }, [employees, search]);

  const updateForm = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const resetForm = () => {
    setForm(getEmptyForm());
    setEditingId("");
  };

  const startEdit = (employee) => {
    setEditingId(employee._id);
    setForm({
      name: employee.name || "",
      email: employee.email || "",
      password: "",
      mobileNumber: employee.mobileNumber || "",
      dateOfBirth: toInputDate(employee.dateOfBirth),
      joiningDate: toInputDate(employee.joiningDate),
      hourlyRate: employee.hourlyRate ?? "",
      role: employee.role || "Designer",
    });
    setError("");
    setSuccess("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitForm = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        mobileNumber: form.mobileNumber.trim(),
        dateOfBirth: form.dateOfBirth || "",
        joiningDate: form.joiningDate || "",
        hourlyRate: Number(form.hourlyRate),
        role: form.role,
        companyName: COMPANY_NAME,
      };

      if (form.password) payload.password = form.password;

      if (editingId) {
        await axios.put(`${API}/api/users/manage/${editingId}`, payload, {
          headers: authHeaders(),
        });
        setSuccess("Employee updated successfully.");
      } else {
        await axios.post(`${API}/api/users/manage`, payload, {
          headers: authHeaders(),
        });
        setSuccess("Employee created. They can now log in with this email and password.");
      }

      resetForm();
      await loadData();
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Unable to save employee."));
    } finally {
      setSaving(false);
    }
  };

  const removeEmployee = async (employee) => {
    const confirmed = window.confirm(`Delete ${employee.name}?`);
    if (!confirmed) return;

    try {
      setError("");
      setSuccess("");

      await axios.delete(`${API}/api/users/manage/${employee._id}`, {
        headers: authHeaders(),
      });

      if (editingId === employee._id) resetForm();
      setSuccess("Employee deleted successfully.");
      await loadData();
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Unable to delete employee."));
    }
  };

  const saveRole = async (event) => {
    event.preventDefault();

    try {
      setRoleSaving(true);
      setRoleError("");

      await axios.post(
        `${API}/api/users/roles`,
        { name: newRole.trim() },
        { headers: authHeaders() }
      );

      const addedRole = newRole.trim();
      setNewRole("");
      setRoleModalOpen(false);
      await loadData();
      updateForm("role", addedRole);
    } catch (requestError) {
      setRoleError(getErrorMessage(requestError, "Unable to add role."));
    } finally {
      setRoleSaving(false);
    }
  };

  const roleOptions = roles.includes(form.role)
    ? roles
    : [...roles, form.role].filter(Boolean);

  return (
    <div className="me-page">
      <header className="me-header">
        <h1>
          <span className="me-header-icon">👥</span>
          Manage Employees / Team Members
        </h1>

        <button
          type="button"
          className="me-btn me-btn-dark"
          onClick={() => {
            setRoleError("");
            setRoleModalOpen(true);
          }}
        >
          + Add Role
        </button>
      </header>

      {error && <div className="me-alert me-alert-error">{error}</div>}
      {success && <div className="me-alert me-alert-success">{success}</div>}

      <form className="me-form" onSubmit={submitForm} autoComplete="off">
        <div className="me-field">
          <label>Employee Name</label>
          <input
            required
            value={form.name}
            onChange={(event) => updateForm("name", event.target.value)}
          />
        </div>

        <div className="me-field">
          <label>Email</label>
          <input
            required
            type="email"
            autoComplete="off"
            value={form.email}
            onChange={(event) => updateForm("email", event.target.value)}
          />
        </div>

        <div className="me-field">
          <label>Password</label>
          <input
            type="password"
            autoComplete="new-password"
            required={!editingId}
            minLength={editingId && !form.password ? undefined : 6}
            placeholder={editingId ? "Leave blank to keep current password" : ""}
            value={form.password}
            onChange={(event) => updateForm("password", event.target.value)}
          />
        </div>

        <div className="me-field">
          <label>Mobile Number</label>
          <input
            value={form.mobileNumber}
            onChange={(event) => updateForm("mobileNumber", event.target.value)}
          />
        </div>

        <div className="me-field">
          <label>🎂 Birth Date</label>
          <input
            type="date"
            value={form.dateOfBirth}
            onChange={(event) => updateForm("dateOfBirth", event.target.value)}
          />
        </div>

        <div className="me-field">
          <label>📅 Joining Date</label>
          <input
            type="date"
            value={form.joiningDate}
            onChange={(event) => updateForm("joiningDate", event.target.value)}
          />
        </div>

        <div className="me-field">
          <label>Hourly Rate</label>
          <input
            required
            type="number"
            min="0"
            step="0.01"
            value={form.hourlyRate}
            onChange={(event) => updateForm("hourlyRate", event.target.value)}
          />
        </div>

        <div className="me-field">
          <label>Company</label>
          <select value={COMPANY_NAME} disabled>
            <option value={COMPANY_NAME}>{COMPANY_NAME}</option>
          </select>
        </div>

        <div className="me-field">
          <label>Role</label>
          <select
            value={form.role}
            onChange={(event) => updateForm("role", event.target.value)}
          >
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>

        <div className="me-form-actions">
          <button type="submit" className="me-btn me-btn-primary" disabled={saving}>
            {saving
              ? "Saving..."
              : editingId
                ? "✔ Update User"
                : "+ Add User"}
          </button>

          {editingId && (
            <button
              type="button"
              className="me-btn me-btn-light"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="me-search">
        <span>🔍</span>
        <input
          placeholder="Search employee..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="me-table-wrap">
        <table className="me-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>DOB</th>
              <th>Joining Date</th>
              <th>Hourly Rate</th>
              <th>Company</th>
              <th>Role</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="me-empty">
                  Loading employees...
                </td>
              </tr>
            ) : filteredEmployees.length ? (
              filteredEmployees.map((employee) => (
                <tr key={employee._id}>
                  <td>{employee.name}</td>
                  <td>{employee.email}</td>
                  <td>{employee.mobileNumber || "-"}</td>
                  <td>{formatDate(employee.dateOfBirth)}</td>
                  <td>{formatDate(employee.joiningDate)}</td>
                  <td>{formatRate(employee.hourlyRate)}</td>
                  <td>{employee.companyName || "-"}</td>
                  <td>{employee.role}</td>
                  <td>
                    <div className="me-actions">
                      <button
                        type="button"
                        className="me-btn me-btn-edit"
                        onClick={() => startEdit(employee)}
                      >
                        ✏️ Edit
                      </button>

                      <button
                        type="button"
                        className="me-btn me-btn-delete"
                        onClick={() => removeEmployee(employee)}
                      >
                        🗑 Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="me-empty">
                  No employees found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {roleModalOpen && (
        <div
          className="me-modal-backdrop"
          onMouseDown={() => !roleSaving && setRoleModalOpen(false)}
        >
          <form
            className="me-modal"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={saveRole}
          >
            <h2>Add Role</h2>

            {roleError && <div className="me-alert me-alert-error">{roleError}</div>}

            <div className="me-field">
              <label>Role name</label>
              <input
                autoFocus
                required
                placeholder="e.g. Site Supervisor"
                value={newRole}
                onChange={(event) => setNewRole(event.target.value)}
              />
            </div>

            <div className="me-modal-actions">
              <button
                type="button"
                className="me-btn me-btn-light"
                onClick={() => setRoleModalOpen(false)}
                disabled={roleSaving}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="me-btn me-btn-primary"
                disabled={roleSaving}
              >
                {roleSaving ? "Saving..." : "Save Role"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ManageEmployees;